import type { FunctionComponent } from "react"

import { clsx } from "clsx"

import { Text } from "@jobteaser/spark/components/Text"

import { KeyInformation } from "@/features/companyDetail/components/shared/KeyInformation/KeyInformation"
import { Parity } from "@/features/companyDetail/components/shared/Parity/Parity"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./DeepDiveKeyInformation.module.css"

export const DeepDiveKeyInformation: FunctionComponent = () => {
  const { t } = useTranslation(["companies_client"])
  const { dataCompany } = useCompanyFetch()

  if (!dataCompany) return null
  const {
    page: {
      creationYear,
      countryCount,
      averageAge,
      employeeParity: { men, women, others },
      shortValues,
    },
  } = dataCompany

  const showParityValue = men > 0 || women > 0 || others > 0
  const showShortValues = (shortValues || []).map(value => value.trim()).filter(Boolean).length > 0

  return (
    <ul className={styles.deepDiveKeyInformation__keysContainer}>
      {creationYear > 0 && (
        <li
          className={styles.deepDiveKeyInformation__key}
          data-testid="company_deep_dive_key_information_creation_year"
        >
          <KeyInformation title={t("companies_client.keyInformation.creationYear.title")}>
            <Text variant="body1" weight="bold">
              {creationYear}
            </Text>
          </KeyInformation>
        </li>
      )}
      {showParityValue && (
        <li className={styles.deepDiveKeyInformation__key} data-testid="company_deep_dive_key_information_parity">
          <KeyInformation title={t("companies_client.keyInformation.parity.title")}>
            <Parity />
          </KeyInformation>
        </li>
      )}
      {!!countryCount && countryCount > 0 && (
        <li
          className={styles.deepDiveKeyInformation__key}
          data-testid="company_deep_dive_key_information_country_count"
        >
          <KeyInformation title={t("companies_client.keyInformation.countryCount.title")}>
            <Text variant="body1" weight="bold">
              {t("companies_client.keyInformation.countryCount.count", { count: countryCount })}
            </Text>
          </KeyInformation>
        </li>
      )}
      {averageAge > 0 && (
        <li className={styles.deepDiveKeyInformation__key} data-testid="company_deep_dive_key_information_average_age">
          <KeyInformation title={t("companies_client.keyInformation.averageAge.title")}>
            <Text variant="body1" weight="bold">
              {t("companies_client.keyInformation.averageAge.age", { age: averageAge })}
            </Text>
          </KeyInformation>
        </li>
      )}
      {showShortValues && (
        <li
          className={clsx(styles.deepDiveKeyInformation__key, styles["deepDiveKeyInformation__key--fullGrid"])}
          data-testid="company_deep_dive_key_information_short_values"
        >
          <KeyInformation title={t("companies_client.keyInformation.shortValues.title")}>
            <Text variant="body1" weight="bold">
              {shortValues.filter(value => value.trim()).join(" • ")}
            </Text>
          </KeyInformation>
        </li>
      )}
    </ul>
  )
}
